import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Select, notification, Typography, Space, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';

import { label as enumLabel } from '../../lib/format';
export const SOURCE_OPTIONS = [
  { value: 'WEBSITE', label: 'Website' },
  { value: 'INTERNAL_WEBSITE', label: 'Internal website' },
  { value: 'REFERRAL_CLIENT', label: 'Referral - client' },
  { value: 'REFERRAL_STAFF', label: 'Referral - staff' },
  { value: 'ASSOCIATE', label: 'Associate' },
  { value: 'OTHER', label: 'Other' },
];
export const LEAD_TYPE_OPTIONS = [
  { value: 'CY_PRIVATE_COMPANY', label: 'CY Private Company (LTD / LLC)' },
  { value: 'CY_PUBLIC_COMPANY', label: 'CY Public Company (PLC)' },
  { value: 'CY_PARTNERSHIP', label: 'CY Partnership' },
  { value: 'FOREIGN_COMPANY', label: 'Foreign company' },
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'OTHER', label: 'Other' },
];
export const LEAD_STATUS_COLORS = { NEW: 'blue', CONTACTED: 'gold', PROPOSAL_SENT: 'purple', CONVERTED: 'green', LOST: 'red' };

const label = (options, v) => options.find((o) => o.value === v)?.label || v;

/** The pipeline of prospective clients; everything else about a lead lives on its detail page. */
export default function LeadsPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [data, setData] = React.useState([]);
  const [associates, setAssociates] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    request
      .get('/associates')
      .then((r) => r.json())
      .then((r) => r.status && setAssociates(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeads = () => {
    if (!companyId) return;
    setLoading(true);
    request
      .get('/leads', { company_id: companyId, ...(statusFilter ? { status: statusFilter } : {}) })
      .then((r) => r.json())
      .then((r) => r.status && setData(r.data))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchLeads, [companyId, statusFilter]);

  const handleCreate = () => {
    form.validateFields().then((values) => {
      request
        .post('/leads', { ...values, company_id: companyId })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Lead created' });
            setModalOpen(false);
            navigate(`/leads/${r.data.id}`);
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const columns = [
    {
      title: 'Lead',
      dataIndex: 'lead_name',
      render: (v, l) => (
        <span>
          <Link to={`/leads/${l.id}`}>{v}</Link>
          {l.business_name && <Typography.Text type="secondary"> ({l.business_name})</Typography.Text>}
        </span>
      ),
    },
    { title: 'Type', dataIndex: 'lead_type', render: (v) => label(LEAD_TYPE_OPTIONS, v) },
    { title: 'Contact', key: 'contact', render: (_, l) => [l.contact_person, l.telephone, l.email].filter(Boolean).join(' - ') || '-' },
    { title: 'Source', dataIndex: 'source', render: (v, l) => (l.Associate ? `${label(SOURCE_OPTIONS, v)} (${l.Associate.name})` : label(SOURCE_OPTIONS, v)) },
    { title: 'Assigned to', key: 'assigned', render: (_, l) => l.AssignedUser?.name || '-' },
    { title: 'Next follow-up', dataIndex: 'next_follow_up_date', sorter: (a, b) => String(a.next_follow_up_date || '9').localeCompare(String(b.next_follow_up_date || '9')), render: (v) => v || '-' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v, l) =>
        v === 'CONVERTED' && l.ConvertedClient ? (
          <Link to={`/clients/${l.ConvertedClient.id}`}>
            <Tag color={LEAD_STATUS_COLORS[v]}>CONVERTED</Tag>
          </Link>
        ) : (
          <Tag color={LEAD_STATUS_COLORS[v]}>{enumLabel(v)}</Tag>
        ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Leads (Prospective Clients)
        </Typography.Title>
        <Space>
          {isSystemAdmin && (
            <Select style={{ width: 200 }} value={companyId} placeholder="Company" onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />
          )}
          <Select
            allowClear
            style={{ width: 170 }}
            placeholder="All statuses"
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.keys(LEAD_STATUS_COLORS).map((s) => ({ value: s, label: s.replace('_', ' ') }))}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setModalOpen(true);
            }}
          >
            New Lead
          </Button>
        </Space>
      </Space>

      <Table rowKey="id" columns={columns} dataSource={data} loading={loading} />

      <Modal title="New Lead" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate} okText="Create and open">
        <Form form={form} layout="vertical">
          <Form.Item name="lead_name" label="Registered name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="business_name" label="Business / brand name">
            <Input maxLength={150} />
          </Form.Item>
          <Form.Item name="lead_type" label="Type" rules={[{ required: true }]} initialValue="CY_PRIVATE_COMPANY">
            <Select options={LEAD_TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="source" label="Source" rules={[{ required: true }]} initialValue="OTHER">
            <Select options={SOURCE_OPTIONS} />
          </Form.Item>
          <Form.Item name="associate_id" label="Associate (if referred)">
            <Select allowClear showSearch optionFilterProp="label" options={associates.map((a) => ({ value: a.id, label: a.name }))} />
          </Form.Item>
          <Form.Item name="contact_person" label="Contact person">
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item name="telephone" label="Telephone">
            <Input maxLength={40} />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
            <Input maxLength={120} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
